import ArchitectureIcon from '@mui/icons-material/Architecture'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ConstructionIcon from '@mui/icons-material/Construction'
import ErrorIcon from '@mui/icons-material/Error'
import GroupsIcon from '@mui/icons-material/Groups'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import WarningIcon from '@mui/icons-material/Warning'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import type { ReactElement } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useDashboardSummary, type SiteToday } from '../api/dashboard'
import { useAuth } from '../auth/useAuth'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'

/**
 * What has and has not been entered today.
 *
 * <p>Deliberately not a chart. The question someone opens this with at seven in the
 * morning is "which of my sites is still empty", and the answer to that is a count and a
 * list, not a trend line. A trend would look more impressive and answer nothing.</p>
 *
 * <p>State is never carried by colour alone: every status has an icon and a word beside
 * it, so it survives a colourblind reader, a monochrome print and direct sunlight on a
 * cheap screen.</p>
 */
export function DashboardPage() {
  const { user } = useAuth()
  const summary = useDashboardSummary()

  const data = summary.data
  const outstanding = data ? data.siteCount - data.sitesComplete : 0

  return (
    <>
      <PageHeader
        title={`Good day, ${user?.fullName?.split(' ')[0] ?? ''}`.trim()}
        subtitle={
          data
            ? new Date(data.date).toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })
            : 'Today'
        }
      />

      {summary.error && <ErrorBlock error={summary.error} />}
      {summary.isLoading && <LoadingBlock label="Checking today across your sites" />}

      {data && (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr 1fr',
                md: 'repeat(4, 1fr)',
              },
              mb: 3,
            }}
          >
            <Stat
              label="Days entered"
              value={`${data.sitesComplete}/${data.siteCount}`}
              caption={
                outstanding === 0
                  ? 'Every site is up to date'
                  : `${outstanding} still to finish`
              }
              tone={outstanding === 0 ? 'good' : data.sitesStarted > 0 ? 'warning' : 'serious'}
            />
            <Stat label="On site" value={data.workersOnSite} caption="workers recorded today" />
            <Stat
              label="Drawings awaited"
              value={data.pendingDrawings}
              caption={data.pendingDrawings === 0 ? 'Nothing outstanding' : 'across your projects'}
              tone={data.pendingDrawings === 0 ? 'good' : 'neutral'}
            />
            <Stat
              label="Overdue"
              value={data.overdueDrawings}
              caption="past the promised date"
              tone={data.overdueDrawings > 0 ? 'serious' : 'good'}
            />
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Today, site by site
          </Typography>

          {data.sites.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary">
                  No sites are assigned to you. An administrator can grant site access under
                  Administration → Users.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {data.sites.map((site) => (
                <SiteRow key={site.siteId} site={site} />
              ))}
            </Box>
          )}

          {data.pendingDrawings > 0 && (
            <Button
              component={RouterLink}
              to="/drawings/pending"
              startIcon={<ArchitectureIcon />}
              sx={{ mt: 2 }}
            >
              Chase {data.pendingDrawings} pending drawing
              {data.pendingDrawings === 1 ? '' : 's'}
            </Button>
          )}
        </>
      )}
    </>
  )
}

type Tone = 'neutral' | 'good' | 'warning' | 'serious'

/**
 * A single number, large enough to read across a desk.
 *
 * <p>The number wears ink, not the status colour — a coloured rule down the edge carries
 * the state instead, so the figure stays legible whatever it means.</p>
 */
function Stat({
  label,
  value,
  caption,
  tone = 'neutral',
}: {
  label: string
  value: string | number
  caption?: string
  tone?: Tone
}) {
  const edge = useToneColour(tone)
  return (
    <Card sx={{ borderLeft: '4px solid', borderColor: edge }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.1, mt: 0.5 }}>
          {value}
        </Typography>
        {caption && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

function SiteRow({ site }: { site: SiteToday }) {
  const tone: Tone = site.complete ? 'good' : site.started ? 'warning' : 'serious'
  const edge = useToneColour(tone)
  const state = site.complete ? 'Complete' : site.started ? 'Part entered' : 'Not started'

  return (
    <Card sx={{ borderLeft: '4px solid', borderColor: edge }}>
      <CardContent sx={{ py: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            mb: 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {site.siteName}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                {site.siteCode}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {site.projectName ?? '—'}
            </Typography>
          </Box>

          {/* Icon and word together: the colour is the third signal, never the only one. */}
          <StateLabel tone={tone} text={state} />

          <Button
            size="small"
            variant={site.complete ? 'text' : 'contained'}
            component={RouterLink}
            to={site.reportId ? `/daily-reports/${site.reportId}` : '/entry/work'}
          >
            {site.reportId ? 'Open day' : 'Start the day'}
          </Button>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Recorded
            icon={<ConstructionIcon fontSize="small" />}
            label="Work"
            count={site.workLines}
            detail={`${site.workLines} line${site.workLines === 1 ? '' : 's'}`}
          />
          <Recorded
            icon={<GroupsIcon fontSize="small" />}
            label="Labour"
            count={site.labourLines}
            detail={`${site.workers} worker${site.workers === 1 ? '' : 's'}`}
          />
          <Recorded
            icon={<LocalShippingIcon fontSize="small" />}
            label="Material"
            count={site.materialReceipts}
            detail={`${site.materialReceipts} deliver${site.materialReceipts === 1 ? 'y' : 'ies'}`}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

/** One of the three daily entries: done, or not yet. */
function Recorded({
  icon,
  label,
  count,
  detail,
}: {
  icon: ReactElement
  label: string
  count: number
  detail: string
}) {
  const theme = useTheme()
  const done = count > 0
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color: done ? theme.palette.success.main : 'text.disabled', display: 'flex' }}>
        {done ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
      </Box>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {done ? detail : 'nothing yet'}
        </Typography>
      </Box>
    </Box>
  )
}

function StateLabel({ tone, text }: { tone: Tone; text: string }) {
  const colour = useToneColour(tone)
  const icon =
    tone === 'good' ? (
      <CheckCircleIcon fontSize="small" />
    ) : tone === 'warning' ? (
      <WarningIcon fontSize="small" />
    ) : (
      <ErrorIcon fontSize="small" />
    )
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: colour }}>
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {text}
      </Typography>
    </Box>
  )
}

/** Status colours come from the theme's reserved set, never from a chart palette. */
function useToneColour(tone: Tone): string {
  const theme = useTheme()
  switch (tone) {
    case 'good':
      return theme.palette.success.main
    case 'warning':
      return theme.palette.warning.main
    case 'serious':
      return theme.palette.error.main
    default:
      return theme.palette.divider
  }
}
